import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Save, Send, Download, Upload, Mail, ShieldCheck, DatabaseBackup, UserPlus, X } from "lucide-react";

export default function Settings() {
  const [form, setForm] = useState({ gmail_address: "", smtp_host: "smtp.gmail.com", smtp_port: 587, sender_name: "", notification_email: "", app_password: "" });
  const [pwSet, setPwSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [backups, setBackups] = useState([]);
  const [access, setAccess] = useState({ allowed_emails: [], primary: "" });
  const [newEmail, setNewEmail] = useState("");
  const [savingAccess, setSavingAccess] = useState(false);

  const loadBackups = () => api.get("/admin/backups").then((r) => setBackups(r.data)).catch(() => {});
  const loadAccess = () => api.get("/admin/access").then((r) => setAccess(r.data)).catch(() => {});
  useEffect(() => { loadBackups(); loadAccess(); }, []);

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Enter a valid email"); return; }
    if (access.allowed_emails.includes(e)) { toast.error("Already added"); return; }
    setAccess((a) => ({ ...a, allowed_emails: [...a.allowed_emails, e] }));
    setNewEmail("");
  };

  const removeEmail = (e) => {
    if (e === access.primary) { toast.error("Primary admin cannot be removed"); return; }
    setAccess((a) => ({ ...a, allowed_emails: a.allowed_emails.filter((x) => x !== e) }));
  };

  const saveAccess = async () => {
    setSavingAccess(true);
    try {
      const { data } = await api.put("/admin/access", { allowed_emails: access.allowed_emails });
      setAccess((a) => ({ ...a, allowed_emails: data.allowed_emails }));
      toast.success("Team access updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingAccess(false); }
  };

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const { app_password_set, ...rest } = r.data;
      setForm((f) => ({ ...f, ...rest, app_password: "" }));
      setPwSet(app_password_set);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
      if (form.app_password) { setPwSet(true); setForm((f) => ({ ...f, app_password: "" })); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/settings/test-email", {});
      toast.success(data.message);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const downloadBackup = async () => {
    try {
      const { data } = await api.get("/admin/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `webvantage-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (err) { toast.error("Backup failed"); }
  };

  const restore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRestoring(true);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const { data } = await api.post("/admin/restore", json);
      toast.success("Restore complete: " + Object.entries(data.restored).map(([k, v]) => `${k}(${v})`).join(", "));
    } catch (err) { toast.error("Restore failed — invalid file"); }
    finally { setRestoring(false); e.target.value = ""; }
  };

  const runBackup = async () => {
    try {
      await api.post("/admin/backups/run");
      toast.success("Backup snapshot created");
      loadBackups();
    } catch { toast.error("Backup failed"); }
  };

  const downloadSnapshot = async (id) => {
    try {
      const { data } = await api.get(`/admin/backups/${id}/download`);
      const blob = new Blob([typeof data === "string" ? data : JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `webvantage-backup-${id.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const inputCls = "mt-2 border-white/10 bg-white/[0.03]";
  if (loading) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-vantage" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-settings">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-white">Settings</h1>
        <p className="mt-1 text-sm text-slate-400">Configure email notifications, security and backups.</p>
      </div>

      {/* Gmail SMTP */}
      <div className="max-w-3xl rounded-2xl border border-white/10 bg-ink-900 p-8">
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-vantage" />
          <h2 className="font-heading text-lg font-semibold text-white">Gmail SMTP & Email Notifications</h2>
        </div>
        <p className="mt-2 text-sm text-slate-400">
          Enter a Gmail address and an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-vantage hover:underline">App Password</a> (not your normal password). New leads and subscribers will be emailed instantly.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div><Label className="text-slate-300">Gmail Address</Label><Input value={form.gmail_address} onChange={set("gmail_address")} placeholder="you@gmail.com" className={inputCls} data-testid="smtp-gmail" /></div>
          <div><Label className="text-slate-300">Sender Name</Label><Input value={form.sender_name} onChange={set("sender_name")} placeholder="Web Vantage Enterprises" className={inputCls} data-testid="smtp-sender" /></div>
          <div><Label className="text-slate-300">SMTP Host</Label><Input value={form.smtp_host} onChange={set("smtp_host")} className={inputCls} data-testid="smtp-host" /></div>
          <div><Label className="text-slate-300">SMTP Port</Label><Input type="number" value={form.smtp_port} onChange={set("smtp_port")} className={inputCls} data-testid="smtp-port" /></div>
          <div><Label className="text-slate-300">App Password</Label><Input type="password" value={form.app_password} onChange={set("app_password")} placeholder={pwSet ? "•••••••• (saved — leave blank to keep)" : "16-character app password"} className={inputCls} data-testid="smtp-password" /></div>
          <div><Label className="text-slate-300">Notify Email (leads to)</Label><Input value={form.notification_email} onChange={set("notification_email")} placeholder="leads@yourdomain.com" className={inputCls} data-testid="smtp-notify" /></div>
        </div>
        <div className="mt-6 flex gap-3">
          <Button onClick={save} disabled={saving} className="bg-vantage hover:bg-vantage-hover" data-testid="settings-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save settings</>}
          </Button>
          <Button onClick={sendTest} disabled={testing} variant="ghost" className="border border-white/10" data-testid="settings-test">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send test email</>}
          </Button>
        </div>
      </div>

      {/* Security */}
      <div className="max-w-3xl rounded-2xl border border-white/10 bg-ink-900 p-8">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-400" />
          <h2 className="font-heading text-lg font-semibold text-white">Security</h2>
        </div>
        <ul className="mt-4 space-y-2 text-sm text-slate-400">
          <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> JWT authentication with bcrypt password hashing</li>
          <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Brute-force protection (5 attempts → 15 min lockout)</li>
          <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Role-based admin access on all management endpoints</li>
          <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> HTTPS / SSL ready, credentials stored server-side (never hardcoded)</li>
        </ul>
      </div>

      {/* Team Access (Google sign-in allow-list) */}
      <div className="max-w-3xl rounded-2xl border border-white/10 bg-ink-900 p-8" data-testid="team-access">
        <div className="flex items-center gap-3">
          <UserPlus className="h-5 w-5 text-vantage" />
          <h2 className="font-heading text-lg font-semibold text-white">Team Access (Google Sign-In)</h2>
        </div>
        <p className="mt-2 text-sm text-slate-400">Only these Google emails can sign into the admin panel. Add or remove teammates anytime — no redeploy needed.</p>
        <div className="mt-6 flex gap-2">
          <Input
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
            placeholder="teammate@company.com"
            className="border-white/10 bg-white/[0.03]"
            data-testid="access-email-input"
          />
          <Button onClick={addEmail} variant="ghost" className="border border-white/10" data-testid="access-add">
            <UserPlus className="h-4 w-4" /> Add
          </Button>
        </div>
        <ul className="mt-4 space-y-2">
          {access.allowed_emails.map((e) => (
            <li key={e} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-4 py-2.5 text-sm" data-testid={`access-row-${e}`}>
              <span className="text-slate-200">{e}{e === access.primary && <span className="ml-2 rounded-full bg-vantage/10 px-2 py-0.5 text-xs text-vantage">primary</span>}</span>
              {e !== access.primary && (
                <button onClick={() => removeEmail(e)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-500/10 hover:text-red-400" data-testid={`access-remove-${e}`}>
                  <X className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
        <Button onClick={saveAccess} disabled={savingAccess} className="mt-6 bg-vantage hover:bg-vantage-hover" data-testid="access-save">
          {savingAccess ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save team access</>}
        </Button>
      </div>

      {/* Backup */}
      <div className="max-w-3xl rounded-2xl border border-white/10 bg-ink-900 p-8">
        <div className="flex items-center gap-3">
          <DatabaseBackup className="h-5 w-5 text-vantage" />
          <h2 className="font-heading text-lg font-semibold text-white">Backup & Restore</h2>
        </div>
        <p className="mt-2 text-sm text-slate-400">Automatic daily backups run in the background. Download a full backup, restore from a file, or grab a saved snapshot below.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={downloadBackup} className="bg-vantage hover:bg-vantage-hover" data-testid="backup-download">
            <Download className="h-4 w-4" /> Download backup
          </Button>
          <Button onClick={runBackup} variant="ghost" className="border border-white/10" data-testid="backup-run">
            <DatabaseBackup className="h-4 w-4" /> Run snapshot now
          </Button>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/5" data-testid="backup-restore-label">
            {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Restore from file
            <input type="file" accept="application/json" className="hidden" onChange={restore} data-testid="backup-restore-input" />
          </label>
        </div>
        {backups.length > 0 && (
          <div className="mt-6 divide-y divide-white/5 rounded-xl border border-white/10">
            {backups.map((b) => (
              <div key={b.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`backup-row-${b.id}`}>
                <div>
                  <span className={`mr-2 rounded-full px-2 py-0.5 text-xs ${b.kind === "auto" ? "bg-emerald-500/10 text-emerald-400" : "bg-vantage/10 text-vantage"}`}>{b.kind}</span>
                  <span className="text-slate-300">{(b.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  <span className="ml-2 text-xs text-slate-500">{(b.size / 1024).toFixed(1)} KB</span>
                </div>
                <button onClick={() => downloadSnapshot(b.id)} className="flex items-center gap-1 text-xs text-vantage hover:underline" data-testid={`backup-dl-${b.id}`}>
                  <Download className="h-3.5 w-3.5" /> Download
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="mt-3 text-xs text-amber-400/80">Restoring replaces existing content in the affected collections.</p>
      </div>
    </div>
  );
}
